/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDashboardRequest,
  GetDashboardSecurity,
} from "../models/operations/getdashboard.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDashboardQuery,
  DashboardQueryData,
  prefetchDashboard,
  queryKeyDashboard,
} from "./dashboard.core.js";
export {
  buildDashboardQuery,
  type DashboardQueryData,
  prefetchDashboard,
  queryKeyDashboard,
};

export type DashboardQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getDashboard dashboards
 *
 * @remarks
 * Get one dashboard by id, with its cards and saved filters.
 */
export function useDashboard(
  request: GetDashboardRequest,
  security?: GetDashboardSecurity | undefined,
  options?: QueryHookOptions<DashboardQueryData, DashboardQueryError>,
): UseQueryResult<DashboardQueryData, DashboardQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDashboardQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getDashboard dashboards
 *
 * @remarks
 * Get one dashboard by id, with its cards and saved filters.
 */
export function useDashboardSuspense(
  request: GetDashboardRequest,
  security?: GetDashboardSecurity | undefined,
  options?: SuspenseQueryHookOptions<DashboardQueryData, DashboardQueryError>,
): UseSuspenseQueryResult<DashboardQueryData, DashboardQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDashboardQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDashboardData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DashboardQueryData,
): DashboardQueryData | undefined {
  const key = queryKeyDashboard(...queryKeyBase);

  return client.setQueryData<DashboardQueryData>(key, data);
}

export function invalidateDashboard(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dashboards", "get", ...queryKeyBase],
  });
}

export function invalidateAllDashboard(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dashboards", "get"],
  });
}
